import { isDefaultRuleSet, type RuleSet } from './get-pype-rule-groups';

/**
 * The value of the option standing for "no rule set explicitly assigned". It is
 * not "no masking": such a channel falls back to the project default
 * (PSTUDIO-3804), which is why the option is labelled after that default and
 * why it exists at all.
 */
export const DEFAULT_RULE_SET_VALUE = '';

export interface MaskingRuleSetOption {
  value: string;
  label: string;
}

export interface MaskingRuleSetSelect {
  options: MaskingRuleSetOption[];
  /** What the Select's `value` must be for the current binding. */
  value: string;
  /**
   * Whether the empty-valued option is offered — i.e. whether the project has a
   * default to fall back to. The caller gates its help text on this rather than
   * re-deriving it from `options`, so both live in one place.
   */
  hasDefault: boolean;
}

/**
 * Builds the Auto-masking dropdown for a channel: its options and the value the
 * current binding maps to.
 *
 * PSTUDIO-4051. The project default used to appear TWICE — once as the
 * empty-valued fallback option ("Default — Standard Rules") and again in the
 * plain list of rule sets, by id. It is now represented only by the empty-valued
 * option, and a channel explicitly bound to that set is NORMALISED onto it, so
 * dropping the by-id copy cannot leave the Select showing nothing.
 *
 * That normalisation is display-only, so the CALLER must compare like with like:
 * feed the channel's persisted binding through this helper too before deciding
 * whether the form is dirty. Comparing a normalised pick against a raw id reads
 * "unchanged" as "changed" and saves an unassign — see `channel-settings-form`.
 *
 * The other half of the ticket is that nothing may be labelled "None": with no
 * default there is nothing an empty value could mean, so no empty-valued option
 * is offered at all. That deliberately also removes the way to UNBIND a channel
 * in such a project — "no rule set at all" is exactly the state the ticket bans;
 * where a default exists, picking it is the unbind (the channel falls back to
 * it).
 *
 * Only the FIRST system rule set is promoted, and only IT normalises: a second
 * SYSTEM set is not expected (the backend seeds one per project), but hiding it
 * would make it unreachable, so it stays a normal option and a binding to it
 * keeps showing that option.
 */
export const getMaskingRuleSetSelect = (
  ruleSets: RuleSet[],
  selectedRuleSetId: string
): MaskingRuleSetSelect => {
  const defaultRuleSet = ruleSets.find(isDefaultRuleSet);

  const options: MaskingRuleSetOption[] = [
    ...(defaultRuleSet
      ? [
          {
            value: DEFAULT_RULE_SET_VALUE,
            label: `${defaultRuleSet.name} (Default)`,
          },
        ]
      : []),
    ...ruleSets
      // An id IS the option's value, and the empty string already belongs to the
      // default option — a set with no id (`group?.id ?? ''` upstream) would
      // duplicate that option and collide with it inside the Select.
      .filter((ruleSet) => ruleSet.id && ruleSet.id !== defaultRuleSet?.id)
      .map((ruleSet) => ({ value: ruleSet.id, label: ruleSet.name })),
  ];

  return {
    options,
    value:
      defaultRuleSet && selectedRuleSetId === defaultRuleSet.id
        ? DEFAULT_RULE_SET_VALUE
        : selectedRuleSetId,
    hasDefault: Boolean(defaultRuleSet),
  };
};

export interface MaskingAssignment {
  /** Whether the user changed the binding — the form's masking dirty-flag. */
  touched: boolean;
  /** What the Select must show: the user's pick, else the persisted binding. */
  selectValue: string;
  /** `assignPypeRuleGroup`'s payload — RAW ids, see below. */
  fromGroupId?: string;
  toGroupId?: string;
}

/**
 * Resolves everything the channel form needs from the masking binding: whether
 * it is dirty, what the Select shows, and the `assignPypeRuleGroup` payload.
 *
 * PSTUDIO-4051. This exists because the dirty-check is the one reader that MUST
 * compare normalised with normalised. `getMaskingRuleSetSelect` collapses an
 * explicit binding to the project default onto the empty-valued option, so a
 * user who re-picks the option their channel already has produces
 * `selected === ''` against a persisted `<systemId>`. Comparing those two raw
 * strings reads "unchanged" as "changed" and saves `from: <systemId>, to:
 * undefined` — a silent unassign that only shows up as the channel's pill
 * vanishing in Data Privacy, and that the dropdown can no longer undo.
 *
 * Doing it HERE rather than in the form is what makes it testable: the
 * comparison and the normalisation that feeds it can no longer drift apart, and
 * a unit test can pin the round trip (bound to SYSTEM → pick another → pick the
 * default back → NOT touched) that no e2e reaches.
 *
 * The payload ids stay RAW on purpose, and that asymmetry is the point:
 * `fromGroupId` names the group the backend must REMOVE the pype from, so it
 * has to be the real id — normalising it to `''` would send `from: undefined`
 * and leave the old membership in place. Normalisation is for COMPARING and
 * DISPLAYING a binding, never for mutating one.
 */
export const resolveMaskingAssignment = (
  ruleSets: RuleSet[],
  initialRuleSetId: string,
  selectedRuleSetId: string | null
): MaskingAssignment => {
  const { value: initialValue } = getMaskingRuleSetSelect(
    ruleSets,
    initialRuleSetId
  );
  const selectValue = selectedRuleSetId ?? initialRuleSetId;

  return {
    touched: selectedRuleSetId !== null && selectedRuleSetId !== initialValue,
    selectValue,
    fromGroupId: initialRuleSetId || undefined,
    toGroupId: selectValue || undefined,
  };
};

/**
 * Every rule set the pype must be REMOVED from before it is added to
 * `toGroupId` — read from the CURRENT groups rather than from the id the form
 * captured when it loaded.
 *
 * The form resolves `fromGroupId` once, at load time, and nothing subscribes to
 * later changes. If a coworker reassigns the same channel in between, that id
 * names a group the pype no longer belongs to: the remove is a no-op, the add
 * succeeds, and the pype ends up in TWO groups at once — after which
 * `initialRuleSetId`'s `.find()` returns whichever comes first and Data Privacy
 * shows the channel under both. Deriving the removals from live membership
 * makes the assignment idempotent and self-correcting instead.
 *
 * `fromGroupId` is still honoured on top of that: it costs one no-op call and
 * covers the reverse race, where the pype was added to a group after this read.
 * The target group is never removed from — that would undo the add.
 */
export const resolveStaleRuleSetIds = ({
  ruleSets,
  pypeId,
  env,
  toGroupId,
  fromGroupId,
}: {
  ruleSets: RuleSet[];
  pypeId: string;
  env: string;
  toGroupId?: string;
  fromGroupId?: string;
}): string[] => {
  const holdsPype = (ruleSet: RuleSet) =>
    ruleSet.pypes.some(
      (pype) =>
        pype.pypeId === pypeId && pype.env.toLowerCase() === env.toLowerCase()
    );

  const ids = ruleSets
    .filter((ruleSet) => ruleSet.id && holdsPype(ruleSet))
    .map((ruleSet) => ruleSet.id);

  if (fromGroupId) {
    ids.push(fromGroupId);
  }

  return [...new Set(ids)].filter((id) => id !== toGroupId);
};
